import { CapacitorSQLite, SQLiteConnection, SQLiteDBConnection } from '@capacitor-community/sqlite';
import { Capacitor } from '@capacitor/core';

class DatabaseService {
  private sqlite: SQLiteConnection;
  private db: SQLiteDBConnection | null = null;
  private readonly DB_NAME = 'elimupepe.db';

  constructor() {
    this.sqlite = new SQLiteConnection(CapacitorSQLite);
  }

  async initialize(): Promise<void> {
    try {
      const platform = Capacitor.getPlatform();
      console.log('📱 Platform:', platform);

      // Create connection
      this.db = await this.sqlite.createConnection(
        this.DB_NAME,
        false,
        'no-encryption',
        1,
        false
      );

      await this.db.open();
      console.log('✅ Database opened successfully');

      // Create tables
      await this.createTables();
    } catch (error) {
      console.error('❌ Database initialization error:', error);
      throw error;
    }
  }

  private async createTables(): Promise<void> {
    const queries = `
      -- Lessons table
      CREATE TABLE IF NOT EXISTS lessons (
        id INTEGER PRIMARY KEY,
        title TEXT NOT NULL,
        content TEXT,
        course_id INTEGER,
        synced INTEGER DEFAULT 0,
        created_at TEXT,
        updated_at TEXT
      );

      -- Quiz results table (for offline submissions)
      CREATE TABLE IF NOT EXISTS quiz_results (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        quiz_id INTEGER NOT NULL,
        student_id INTEGER NOT NULL,
        answers TEXT NOT NULL,
        score REAL,
        synced INTEGER DEFAULT 0,
        created_at TEXT,
        submitted_at TEXT
      );

      -- Student progress table
      CREATE TABLE IF NOT EXISTS progress (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        student_id INTEGER NOT NULL,
        lesson_id INTEGER NOT NULL,
        completed INTEGER DEFAULT 0,
        progress_percent REAL DEFAULT 0,
        synced INTEGER DEFAULT 0,
        updated_at TEXT
      );

      -- Sync queue table
      CREATE TABLE IF NOT EXISTS sync_queue (

      -- Courses table
      CREATE TABLE IF NOT EXISTS courses (
        id INTEGER PRIMARY KEY,
        title TEXT NOT NULL,
        description TEXT,
        thumbnail TEXT,
        downloaded INTEGER DEFAULT 0,
        created_at TEXT
      );

      -- Downloaded courses tracker
      CREATE TABLE IF NOT EXISTS downloaded_courses (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        course_id INTEGER NOT NULL,
        downloaded_at TEXT,
        last_synced TEXT
      );

      -- Quizzes table
      CREATE TABLE IF NOT EXISTS quizzes (
        id INTEGER PRIMARY KEY,
        lesson_id INTEGER NOT NULL,
        title TEXT NOT NULL,
        questions TEXT NOT NULL,
        time_limit INTEGER,
        downloaded INTEGER DEFAULT 1
      );

      -- User sessions table
      CREATE TABLE IF NOT EXISTS user_sessions (
        id INTEGER PRIMARY KEY,
        user_data TEXT NOT NULL,
        token TEXT NOT NULL,
        created_at TEXT
      );
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        table_name TEXT NOT NULL,
        record_id INTEGER NOT NULL,
        action TEXT NOT NULL,
        data TEXT,
        created_at TEXT
      );
    `;

    await this.db?.execute(queries);
    console.log('✅ Tables created successfully');
  }

  // Save lesson for offline access
  async saveLesson(lesson: any): Promise<void> {
    const query = `
      INSERT OR REPLACE INTO lessons (id, title, content, course_id, created_at, updated_at)
      VALUES (?, ?, ?, ?, ?, ?);
    `;
    await this.db?.run(query, [
      lesson.id,
      lesson.title,
      lesson.content,
      lesson.course_id,
      lesson.created_at,
      lesson.updated_at
    ]);
  }

  // Get offline lessons
  async getOfflineLessons(): Promise<any[]> {
    const result = await this.db?.query('SELECT * FROM lessons;');
    return result?.values || [];
  }

  // Save quiz result offline
  async saveQuizResult(quizResult: any): Promise<number> {
    const query = `
      INSERT INTO quiz_results (quiz_id, student_id, answers, score, created_at, submitted_at)
      VALUES (?, ?, ?, ?, datetime('now'), datetime('now'));
    `;
    const result = await this.db?.run(query, [
      quizResult.quiz_id,
      quizResult.student_id,
      JSON.stringify(quizResult.answers),
      quizResult.score
    ]);
    return result?.changes?.lastId || 0;
  }

  // Get unsynced quiz results
  async getUnsyncedQuizResults(): Promise<any[]> {
    const result = await this.db?.query('SELECT * FROM quiz_results WHERE synced = 0;');
    return result?.values || [];
  }

  // Mark quiz result as synced
  async markQuizResultSynced(id: number): Promise<void> {
    await this.db?.run('UPDATE quiz_results SET synced = 1 WHERE id = ?;', [id]);
  }

  // Update student progress
  async updateProgress(studentId: number, lessonId: number, progressPercent: number): Promise<void> {
    const query = `
      INSERT OR REPLACE INTO progress (student_id, lesson_id, progress_percent, updated_at)
      VALUES (?, ?, ?, datetime('now'));
    `;
    await this.db?.run(query, [studentId, lessonId, progressPercent]);
  }

  // Close database
  async close(): Promise<void> {
    if (this.db) {
      await this.sqlite.closeConnection(this.DB_NAME, false);
      this.db = null;
    }
  }
}

export const dbService = new DatabaseService();

  // Add these tables to createTables() method:
  /*
      -- Courses table
      CREATE TABLE IF NOT EXISTS courses (
        id INTEGER PRIMARY KEY,
        title TEXT NOT NULL,
        description TEXT,
        thumbnail TEXT,
        downloaded INTEGER DEFAULT 0,
        created_at TEXT
      );

      -- Downloaded courses tracker
      CREATE TABLE IF NOT EXISTS downloaded_courses (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        course_id INTEGER NOT NULL,
        downloaded_at TEXT,
        last_synced TEXT
      );

      -- Quizzes table
      CREATE TABLE IF NOT EXISTS quizzes (
        id INTEGER PRIMARY KEY,
        lesson_id INTEGER NOT NULL,
        title TEXT NOT NULL,
        questions TEXT NOT NULL,
        time_limit INTEGER,
        downloaded INTEGER DEFAULT 1
      );
  */
